/* eslint-disable no-nested-ternary */
import React from 'react';

import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import theme from '@styles/theme';

import { InformationAmountCardText, TooltipToModal } from '@components';
import { REGIONS } from '@constants/regions';
import { getEnv } from '@config';

const { country } = getEnv();

const fontTypes = {
  title: css`
    ${({ theme }) => theme.textTypes['Subtitle-1-w500']};
    line-height: 1.19;
    color: ${theme.colors.gray700};
    font-weight: 500;
  `,
  sign: css`
    ${({ theme }) => theme.textTypes['H5-w400']};
    color: ${theme.colors.gray600};
  `,
  mount: css`
    ${({ theme }) => theme.textTypes['H5-w400']};
    line-height: normal;
    color: ${theme.colors.gray800};
  `,
  link: css`
    ${({ theme }) => theme.textTypes['Body-2-w400']};
    line-height: 1.14;
    color: ${theme.colors.bbrand500};
    text-decoration-color: ${theme.colors.bbrand500};
    text-decoration: none;
  `,
  helpLink: css`
    ${({ theme }) => theme.textTypes['Subtitle-1-w400']};
    line-height: 0.69;
    color: #ffffff;
  `,
};

const LayoutHelpLink = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    margin-bottom: 16px;
    margin-right: 8px;
    width: 100%;
    ${theme.breakpoint('md')} {
      width: 100%;
      margin-right: 16px;
      &:last-child {
        margin-right: 0;
      }
    }
    ${theme.breakpoint('ll')} {
      margin-right: 18px;
      max-width: 296px;
    }    
  `
);

const Layout = styled.div(
  ({ theme }) => css`
    display: flex;
    width: 256px;
    border-radius: 6px;
    background-color: ${theme.colors.white};
    padding: 16px 24px;
    flex-direction: column;
    height: 136px;
    z-index: 1;
    ${theme.breakpoint('md')} {
      padding: 23px 25px 15px 23px;
      height: 143px;
      width: 295px;
    }
    ${theme.breakpoint('ll')} {
      max-width: 295px;
    }
    ${(props) =>
      props.isBlock &&
      css`
        padding: 12px 25px 15px 23px !important;
        background-color: ${theme.colors.shadowBlack};
      `}
  `
);

const LayoutTitles = styled.div(
  ({ theme }) => css`    
    display: flex;    
    max-width: 180px;    
    white-space: pre-wrap;
    white-space: -moz-pre-wrap;
    white-space: -o-pre-wrap;    
    ${fontTypes.title}
    justify-content: space-between;    
    align-content: center;
    font-size: 14px;
    ${theme.breakpoint('md')} {      
      width: 100%;
      max-width: 231px;
      line-height: 1.5;
    }
    ${theme.breakpoint('ll')} {
      line-height: 1.5;
      max-width: 231px;      
    }
  `
);

const LayoutMount = styled.div(
  ({ theme }) => css`
    display: flex;
    justify-content: center;
    width: 100%;
    align-items: center;
    margin-top: 16px;
    ${theme.breakpoint('md')} {
      margin-top: 14px;
      align-items: center;
    }
  `
);

const SignMount = styled.div(
  ({ theme }) => css`
    ${fontTypes.sign}
    ${theme.breakpoint('md')} {
      font-size: 20px;
      line-height: 1.6;
    }
  `
);

const Mount = styled.div(
  ({ theme }) => css`
    ${fontTypes.mount}
    margin-left: 3px;
    ${theme.breakpoint('md')} {
      line-height: 1.33;
    }
  `
);

const LayoutTitleIcon = styled.div(
  ({ theme }) => css`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    ${theme.breakpoint('md')} {
      align-items: end;
    }
    ${(props) =>
      props.isBlock &&
      css`
        align-items: center;
        padding-top: 4px;
      `}
  `
);

const LayoutLink = styled.div(
  ({ theme }) => css`
    display: flex;
    ${fontTypes.link}
    margin-top: 16px;
    justify-content: center;
    text-align: center;
    a {
      ${fontTypes.link}
      text-decoration-color: ${theme.colors.bbrand500};
      color: ${theme.colors.bbrand500};
      text-decoration: none;
      text-align: center;
      font-size: 12px;
      ${theme.breakpoint('ll')} {
        line-height: 1.33;
      }
    }
    ${theme.breakpoint('md')} {
      display: -webkit-box;
      max-width: 104px;      
      overflow: hidden;
      text-overflow: ellipsis;      
      margin-top: 19px;
      min-width: -webkit-fill-available;
    }    
  `
);

const ButtonHelpLink = styled.button(
  ({ theme }) => css`
    border: ${theme.colors.gbrand500} 6px solid;
    border-bottom: 0;
    background: ${theme.colors.gbrand500};
    border-radius: 0 0 6px 6px;
    height: 42px;
    margin-top: -7px !important;
    width: 256px;
    ${theme.breakpoint('md')} {
      width: 295px;
    }
  `
);

const HelpLink = styled.div(
  ({ theme }) => css`
    background: ${theme.colors.gbrand500};
    border-radius: 0 0 6px 6px;
    cursor: pointer;
    height: 48px;
    margin-top: -13px;
    z-index: 0;
    text-align: center;
    padding-top: 26px;
    width: 100%;    
    ${theme.breakpoint('md')} {     
      display: flex;      
      padding-left: 14px;
      padding-right: 14px;
      text-align: center;
      align-items: center;
      max-width: 295px;
      justify-content: space-between;
      padding-top: 14px;
    }   
  `
);

const HelpText = styled.div(
  ({ theme }) => css`
    ${fontTypes.helpLink}
    background: ${theme.colors.gbrand500};
    text-align: center;
    width: 100%;
    justify-content: center;
    font-size: 12px;
    ${theme.breakpoint('md')} {           
      display: flex;
      font-weight: normal;
      text-align: center;
      border-radius: 0;
    }    
  `
);

const WhiteLink = styled(LayoutLink)`
  color: #ffffff;
`;

const AmountCard = ({
  amount,
  handleClick,
  handleClickHelpLink,
  handleClickLink,
  helpLink,
  link,
  title,
  to,
  tooltipText,
  tooltipPosition,
  textNoSales,
  InformationAmountCardTextNoSales,
  InformationAmountCardTextError,
  merchantOn,
  tooltipOff,
  showHelpLink,
  htmlIdShowHelpLink,
  htmlIdLink = 'unidentified-id',
  htmlIdTooltip,
  isBlock,
  loadedAmounts
}) => {
  return (
    <LayoutHelpLink>
      <Layout isBlock={isBlock}>
        <LayoutTitleIcon isBlock={isBlock}>
          <LayoutTitles>{title}</LayoutTitles>
          {(!tooltipOff || tooltipOff === false) && (
            <TooltipToModal
              htmlIdTooltip={htmlIdTooltip}
              handleClick={handleClick}
              tooltipPosition={tooltipPosition}
              tooltipText={tooltipText}
              paddingTopLG="3px"
              id="tooltipAmountCard"
              iconName="tooltip-info"
              width={isBlock ? '18px' : '16px'}
              height={isBlock ? '18px' : '16px'}
              color={isBlock ? theme.colors.rblock100 : theme.colors.gray500}
            />
          )}
        </LayoutTitleIcon>
        <div className="layout">
          {textNoSales ? (
            <InformationAmountCardText>
              {InformationAmountCardTextNoSales}
            </InformationAmountCardText>
          ) : amount === 'error' || !loadedAmounts ? (
            <InformationAmountCardText>{InformationAmountCardTextError}</InformationAmountCardText>
          ) : (
            <LayoutMount>
              <SignMount>{REGIONS[country].currencySymbol}</SignMount>
              <Mount>{amount}</Mount>
            </LayoutMount>
          )}
        </div>
        {textNoSales ? (
          <WhiteLink>.</WhiteLink>
        ) : amount === 'error' || !loadedAmounts ? (
          <WhiteLink>.</WhiteLink>
        ) : (
          <LayoutLink>
            <Link onClick={handleClickLink} to={to} id={htmlIdLink}>
              {link}
            </Link>
          </LayoutLink>
        )}
      </Layout>

      {helpLink && textNoSales && merchantOn && (
        <HelpLink onClick={handleClickHelpLink}>
          <HelpText>{helpLink}</HelpText>
        </HelpLink>
      )}

      {helpLink && handleClickHelpLink && showHelpLink && (
        <ButtonHelpLink id={htmlIdShowHelpLink} onClick={handleClickHelpLink}>
          <HelpText>{helpLink}</HelpText>
        </ButtonHelpLink>
      )}

    </LayoutHelpLink>
  );
};

export default AmountCard;
